import { adminRequest, type AdminResult } from "./adminApi";
import type { UnitOwnerResponse } from "./entitlementAdmin";

const groupsPath = "/api/entitlement/groups";

export interface EntitlementGroupSummary {
  id: string;
  name: string;
  parentId: string | null;
  departmentId: string | null;
  monthlyAllowanceUsd: number | null;
  archivedAt: string | null;
  revision: number;
  directMemberCount: number;
  childCount: number;
  /** The Backstage name the group was imported under; read-only in the portal. */
  backstageName?: string | null;
  /** The recorded manager's organisational address; read-only in the portal. */
  managerEmail?: string | null;
  /** Whether the caller may change this group. False for a parent the caller may only see; a missing value means it may. */
  canManage?: boolean;
}

export interface GroupMutationResponse {
  id: string;
  displayName?: string;
  name?: string;
  parentId: string | null;
  monthlyAllowanceUsd: number | null;
  archivedAt: string | null;
  revision: number;
  affectedUserAllowances?: GroupAllowanceImpact[];
}

export interface MembershipMutationResponse {
  personId: string;
  groupId: string;
  add: boolean;
  membershipId: string | null;
  affectedUserAllowances: GroupAllowanceImpact[];
}

export interface EntitlementGroupMember {
  membershipId: string;
  personId: string;
  email: string;
  displayName: string;
  status: string;
  addedAt: string;
  removedAt: string | null;
  /** False when the group's monthly allowance total does not include this member's allowance, so the member is shown muted. */
  contributesToAllowance: boolean;
}

export interface GroupAllowanceImpact {
  personId: string;
  email: string;
  beforeUsd: number;
  afterUsd: number;
}

export interface GroupImpactPreview {
  revisionSnapshot: string;
  groupRevision: number;
  affectedGroupIds: string[];
  users: GroupAllowanceImpact[];
  group?: string;
  parentId?: string | null;
  monthlyAllowanceUsd?: number | null;
}

export interface BulkMembershipOutcome {
  email: string;
  status: "added" | "already present" | "invalid" | "unknown";
  personId: string | null;
}

export interface GroupChange {
  parentId: string | null;
  monthlyAllowanceUsd: number | null;
}

export async function fetchEntitlementGroups(includeArchived = false): Promise<AdminResult<EntitlementGroupSummary[]>> {
  return adminRequest<EntitlementGroupSummary[]>(`${groupsPath}?includeArchived=${String(includeArchived)}`, { method: "GET" }, "group list");
}

export async function createEntitlementGroup(name: string, parentId: string | null, monthlyAllowanceUsd: number | null): Promise<AdminResult<GroupMutationResponse>> {
  return adminRequest<GroupMutationResponse>(groupsPath, { method: "POST", body: JSON.stringify({ name, parentId, monthlyAllowanceUsd }) }, "group creation");
}

export async function renameEntitlementGroup(groupId: string, name: string, expectedRevision: number): Promise<AdminResult<GroupMutationResponse>> {
  return adminRequest<GroupMutationResponse>(`${groupsPath}/${encodeURIComponent(groupId)}`, { method: "PUT", body: JSON.stringify({ name, expectedRevision }) }, "group rename");
}

export async function previewEntitlementGroupChange(groupId: string, change: GroupChange): Promise<AdminResult<GroupImpactPreview>> {
  return adminRequest<GroupImpactPreview>(`${groupsPath}/${encodeURIComponent(groupId)}/preview`, { method: "POST", body: JSON.stringify(change) }, "group change preview");
}

export async function confirmEntitlementGroupChange(groupId: string, change: GroupChange, preview: GroupImpactPreview): Promise<AdminResult<GroupMutationResponse>> {
  return adminRequest<GroupMutationResponse>(`${groupsPath}/${encodeURIComponent(groupId)}/confirm`, {
    method: "POST",
    body: JSON.stringify({ ...change, revisionSnapshot: preview.revisionSnapshot, expectedRevision: preview.groupRevision }),
  }, "group change confirmation");
}

/** Deletes an empty group; the server refuses one with members or child groups, or one imported from Backstage. */
export async function deleteEntitlementGroup(groupId: string, expectedRevision: number): Promise<AdminResult<undefined>> {
  return adminRequest<undefined>(`${groupsPath}/${encodeURIComponent(groupId)}?expectedRevision=${expectedRevision}`, { method: "DELETE" }, "group deletion");
}

/** The total monthly allowance of a group and its non-archived descendants, as returned by `GET /api/entitlement/groups/{groupId}/allowance-total`. */
export interface GroupAllowanceTotal {
  /** Null when the group has no active members beneath it, so its total is shown empty. */
  totalAllowanceUsd: number | null;
}

/** Calls `GET /api/entitlement/groups/{groupId}/allowance-total`. */
export async function fetchEntitlementGroupAllowanceTotal(groupId: string): Promise<AdminResult<GroupAllowanceTotal>> {
  return adminRequest<GroupAllowanceTotal>(`${groupsPath}/${encodeURIComponent(groupId)}/allowance-total`, { method: "GET" }, "group allowance total");
}

/** One row of the bulk allowance totals. A null total means the row has no active members beneath it, so its cell is empty. */
export interface AllowanceTotal {
  id: string;
  totalAllowanceUsd: number | null;
}

/** The allowance totals of every group, Department and Division, as returned by `GET /api/entitlement/groups/allowance-totals`. */
export interface EntitlementAllowanceTotals {
  groups: AllowanceTotal[];
  departments: AllowanceTotal[];
  divisions: AllowanceTotal[];
}

/** Calls `GET /api/entitlement/groups/allowance-totals`: one request that covers every row of the group hierarchy. */
export async function fetchEntitlementAllowanceTotals(): Promise<AdminResult<EntitlementAllowanceTotals>> {
  return adminRequest<EntitlementAllowanceTotals>(`${groupsPath}/allowance-totals`, { method: "GET" }, "allowance totals");
}


export async function fetchEntitlementGroupMembers(groupId: string, includeRemoved = false): Promise<AdminResult<EntitlementGroupMember[]>> {
  return adminRequest<EntitlementGroupMember[]>(`${groupsPath}/${encodeURIComponent(groupId)}/members?includeRemoved=${String(includeRemoved)}`, { method: "GET" }, "group members");
}

export async function previewEntitlementMembership(groupId: string, personId: string, add: boolean): Promise<AdminResult<GroupImpactPreview>> {
  return adminRequest<GroupImpactPreview>(`${groupsPath}/${encodeURIComponent(groupId)}/members/preview`, { method: "POST", body: JSON.stringify({ personId, add }) }, "membership preview");
}

export async function confirmEntitlementMembership(groupId: string, personId: string, add: boolean, revisionSnapshot: string): Promise<AdminResult<MembershipMutationResponse>> {
  return adminRequest<MembershipMutationResponse>(`${groupsPath}/${encodeURIComponent(groupId)}/members/confirm`, { method: "POST", body: JSON.stringify({ personId, add, revisionSnapshot }) }, "membership confirmation");
}

export async function bulkAddEntitlementGroupMembers(groupId: string, expectedRevision: number, emails: string[]): Promise<AdminResult<BulkMembershipOutcome[]>> {
  return adminRequest<BulkMembershipOutcome[]>(`${groupsPath}/${encodeURIComponent(groupId)}/members/bulk`, { method: "POST", body: JSON.stringify({ expectedRevision, emails }) }, "bulk membership update");
}
/** Calls `PUT /api/entitlement/groups/{groupId}/owner`. Saves on the call with no preview; refused with 409 for a group imported from Backstage. */
export async function setEntitlementGroupOwner(groupId: string, personId: string | null): Promise<AdminResult<UnitOwnerResponse>> {
  return adminRequest<UnitOwnerResponse>(`${groupsPath}/${encodeURIComponent(groupId)}/owner`, { method: "PUT", body: JSON.stringify({ personId }) }, "group owner");
}
